import { useEffect, useMemo, useState } from 'react'
import Button from '../components/ui/Button'
import { Card } from '../components/ui/Card'
import { useWallet } from '../wallet/walletContext'

type ChainId = 'solana' | 'base' | 'polygon'
type AssetId = 'SOL' | 'USDC' | 'ETH' | 'MATIC'
type AddressKind = 'solana' | 'evm' | 'unknown'

type PlanStep = {
  id: string
  title: string
  chain: ChainId
  note: string
}

type ExecutionPlan = {
  orderId: string
  pay: AssetOptionId
  target: AssetOptionId
  recipient: string
  amount: string
  steps: PlanStep[]
}

type ReceiptStep = {
  id: string
  title: string
  status: 'CONFIRMED'
  txHash: string
}

type Receipt = {
  orderId: string
  status: 'CONFIRMED'
  delivered: string
  totalFees: string
  steps: ReceiptStep[]
}

const chainLabel: Record<ChainId, string> = {
  solana: 'Solana',
  base: 'Base',
  polygon: 'Polygon',
}

const assetLabel: Record<AssetId, string> = {
  SOL: 'SOL',
  USDC: 'USDC',
  ETH: 'ETH',
  MATIC: 'MATIC',
}

const chainNative: Record<ChainId, AssetId> = {
  solana: 'SOL',
  base: 'ETH',
  polygon: 'MATIC',
}

function randomId() {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function shorten(s: string, head = 6, tail = 4) {
  const v = s.trim()
  if (v.length <= head + tail + 3) return v
  return `${v.slice(0, head)}…${v.slice(-tail)}`
}

function isEvmAddress(s: string) {
  return /^0x[a-fA-F0-9]{40}$/.test(s.trim())
}

function isLikelySolanaAddress(s: string) {
  const v = s.trim()
  if (v.length < 32 || v.length > 44) return false
  if (/[^1-9A-HJ-NP-Za-km-z]/.test(v)) return false
  return true
}

function detectAddressKind(recipient: string): AddressKind {
  if (isEvmAddress(recipient)) return 'evm'
  if (isLikelySolanaAddress(recipient)) return 'solana'
  return recipient.trim().length === 0 ? 'unknown' : 'unknown'
}

function formatFeeUsd(feeUsd: number) {
  if (!Number.isFinite(feeUsd) || feeUsd <= 0) return '—'
  if (feeUsd < 0.01) return '< $0.01'
  return `$${feeUsd.toFixed(2)}`
}

function estimateFeeUsd(chain: ChainId, payAsset: AssetId) {
  const baseFeeUsd = chain === 'solana' ? 0.005 : chain === 'polygon' ? 0.04 : 0.25
  const native = chainNative[chain]
  const extra = payAsset === native ? 0 : 0.15
  return baseFeeUsd + extra
}

type AssetOptionId = `${AssetId}@${ChainId}`

type AssetOption = {
  id: AssetOptionId
  chain: ChainId
  asset: AssetId
  label: string
}

const assetOptions: AssetOption[] = [
  { id: 'SOL@solana', chain: 'solana', asset: 'SOL', label: 'SOL (Solana)' },
  { id: 'USDC@solana', chain: 'solana', asset: 'USDC', label: 'USDC (Solana)' },
  { id: 'USDC@base', chain: 'base', asset: 'USDC', label: 'USDC (Base)' },
  { id: 'USDC@polygon', chain: 'polygon', asset: 'USDC', label: 'USDC (Polygon)' },
  { id: 'ETH@base', chain: 'base', asset: 'ETH', label: 'ETH (Base)' },
  { id: 'MATIC@polygon', chain: 'polygon', asset: 'MATIC', label: 'MATIC (Polygon)' },
]

function parseOption(id: AssetOptionId) {
  const [asset, chain] = id.split('@') as [AssetId, ChainId]
  return { asset, chain }
}

function chainFromEvmChainId(chainId?: string): ChainId | null {
  if (!chainId) return null
  const v = chainId.toLowerCase()
  if (v === '0x2105') return 'base'
  if (v === '0x89') return 'polygon'
  return null
}

function getTargetOptions(kind: AddressKind): AssetOption[] {
  if (kind === 'solana') return assetOptions.filter((o) => o.chain === 'solana' && (o.asset === 'SOL' || o.asset === 'USDC'))
  if (kind === 'evm') return assetOptions.filter((o) => o.chain === 'base' || o.chain === 'polygon').filter((o) => o.asset === 'USDC')
  return assetOptions.filter((o) => o.asset === 'USDC' || o.asset === 'SOL')
}

function getPayOptions(wallet: ReturnType<typeof useWallet>): AssetOption[] {
  const c = wallet.connection
  if (!c) return assetOptions.filter((o) => ['SOL@solana', 'USDC@solana', 'ETH@base', 'USDC@base', 'MATIC@polygon', 'USDC@polygon'].includes(o.id))
  if (c.kind === 'solana') return assetOptions.filter((o) => o.chain === 'solana' && (o.asset === 'SOL' || o.asset === 'USDC'))
  const chain = chainFromEvmChainId(c.chainId) ?? 'base'
  const allowed = chain === 'base' ? (['ETH@base', 'USDC@base'] as AssetOptionId[]) : (['MATIC@polygon', 'USDC@polygon'] as AssetOptionId[])
  return assetOptions.filter((o) => allowed.includes(o.id))
}

function recommendedPayOption(options: AssetOption[]) {
  let best = options[0]
  let bestFee = estimateFeeUsd(best.chain, best.asset)
  for (const o of options.slice(1)) {
    const fee = estimateFeeUsd(o.chain, o.asset)
    if (fee < bestFee) {
      best = o
      bestFee = fee
    }
  }
  return { option: best, feeUsd: bestFee }
}

function planBadge(plan: ExecutionPlan) {
  const hasBridge = plan.steps.some((s) => s.title === 'Bridge')
  const hasSwap = plan.steps.some((s) => s.title === 'Swap')
  if (!hasSwap && !hasBridge) return 'Direct pay'
  if (hasSwap && hasBridge) return 'Swap + bridge + pay'
  return 'Swap + pay'
}

export default function PaymentDemoPage() {
  const wallet = useWallet()

  const [recipient, setRecipient] = useState('')
  const [amount, setAmount] = useState('1.0')

  const addressKind = useMemo(() => detectAddressKind(recipient), [recipient])

  const targetOptions = useMemo(() => getTargetOptions(addressKind), [addressKind])
  const payOptions = useMemo(() => getPayOptions(wallet), [wallet])

  const [target, setTarget] = useState<AssetOptionId>('USDC@base')
  const [pay, setPay] = useState<AssetOptionId>('SOL@solana')

  const [plan, setPlan] = useState<ExecutionPlan | null>(null)
  const [receipt, setReceipt] = useState<Receipt | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!targetOptions.some((o) => o.id === target)) setTarget(targetOptions[0]?.id ?? 'USDC@base')
  }, [target, targetOptions])

  useEffect(() => {
    if (!payOptions.some((o) => o.id === pay)) {
      const rec = recommendedPayOption(payOptions)
      setPay(rec.option.id)
    }
  }, [pay, payOptions])

  const canGenerate = recipient.trim().length > 0 && Number(amount) > 0 && target.trim().length > 0 && pay.trim().length > 0

  const feeInfo = useMemo(() => {
    const selected = assetOptions.find((o) => o.id === pay) ?? payOptions[0]
    const rec = recommendedPayOption(payOptions)
    return {
      selectedFeeUsd: estimateFeeUsd(selected.chain, selected.asset),
      recommended: rec,
      selected,
    }
  }, [pay, payOptions])

  function generatePlan() {
    setError(null)
    setReceipt(null)
    if (!canGenerate) {
      setPlan(null)
      setError('Enter a recipient address and a positive amount.')
      return
    }

    const orderId = randomId()
    const steps: PlanStep[] = []

    const payParsed = parseOption(pay)
    const targetParsed = parseOption(target)
    const sameChain = payParsed.chain === targetParsed.chain
    const nativeFeeAsset = chainNative[payParsed.chain]
    const feeNote = payParsed.asset === nativeFeeAsset ? `${assetLabel[payParsed.asset]} (native fee)` : `${assetLabel[payParsed.asset]} (via conversion)`

    if (sameChain && payParsed.asset === targetParsed.asset) {
      steps.push({
        id: 'p1',
        title: 'Pay',
        chain: targetParsed.chain,
        note: `Send ${amount} ${assetLabel[targetParsed.asset]} to ${shorten(recipient)} · Fees: ${feeNote}`,
      })
    } else if (sameChain) {
      steps.push({
        id: 's1',
        title: 'Swap',
        chain: payParsed.chain,
        note: `Swap ${assetLabel[payParsed.asset]} → ${assetLabel[targetParsed.asset]}`,
      })
      steps.push({
        id: 's2',
        title: 'Pay',
        chain: targetParsed.chain,
        note: `Send ${amount} ${assetLabel[targetParsed.asset]} to ${shorten(recipient)} · Fees: ${feeNote}`,
      })
    } else {
      if (payParsed.asset !== 'USDC') {
        steps.push({
          id: 's1',
          title: 'Swap',
          chain: payParsed.chain,
          note: `Swap ${assetLabel[payParsed.asset]} → USDC`,
        })
      }
      steps.push({
        id: payParsed.asset !== 'USDC' ? 's2' : 's1',
        title: 'Bridge',
        chain: targetParsed.chain,
        note: `Move USDC to ${chainLabel[targetParsed.chain]}`,
      })
      steps.push({
        id: payParsed.asset !== 'USDC' ? 's3' : 's2',
        title: 'Pay',
        chain: targetParsed.chain,
        note: `Send ${amount} ${assetLabel[targetParsed.asset]} to ${shorten(recipient)}`,
      })
    }

    setPlan({
      orderId,
      pay,
      target,
      recipient: recipient.trim(),
      amount,
      steps,
    })
  }

  function runDemoExecution() {
    if (!plan) return
    const payParsed = parseOption(plan.pay)
    const targetParsed = parseOption(plan.target)
    const steps: ReceiptStep[] = plan.steps.map((s) => ({
      id: s.id,
      title: `${s.title} (${chainLabel[s.chain]})`,
      status: 'CONFIRMED',
      txHash: randomId().replaceAll('-', '').slice(0, 32),
    }))
    const totalFeeUsd = estimateFeeUsd(payParsed.chain, payParsed.asset)
    setReceipt({
      orderId: plan.orderId,
      status: 'CONFIRMED',
      delivered: `${plan.amount} ${assetLabel[targetParsed.asset]} to ${shorten(plan.recipient)}`,
      totalFees: `${formatFeeUsd(totalFeeUsd)} (est.)`,
      steps,
    })
  }

  return (
    <main className="bg-hero-radial">
      <section className="container-x py-14">
        <div className="glass p-8">
          <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
            <div>
              <h1 className="text-3xl font-semibold tracking-tight">Payment Demo</h1>
              <p className="mt-2 max-w-2xl text-white/65">Enter recipient, target asset, and pay asset. We generate a plan and a demo receipt.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="ghost" onClick={() => (globalThis.location.href = '/#how')}>
                How it works
              </Button>
              <Button variant="secondary" onClick={() => (globalThis.location.href = '/#cta')}>
                Partner / Join
              </Button>
            </div>
          </div>

          <div className="mt-8 grid gap-5 md:grid-cols-12">
            <Card variant="glass2" className="p-7 md:col-span-5">
              <div className="text-sm font-semibold text-white/90">Inputs</div>
              <div className="mt-4 grid gap-3">
                <label className="grid gap-2">
                  <span className="text-xs text-white/60">Recipient address</span>
                  <input
                    aria-label="Recipient address"
                    value={recipient}
                    onChange={(e) => setRecipient(e.target.value)}
                    placeholder="0x… or Solana address"
                    className="h-11 rounded-xl border border-white/10 bg-black/20 px-4 text-sm text-white outline-none transition focus:border-primary/40"
                  />
                </label>

                <div className="rounded-xl border border-white/10 bg-white/5 p-3 text-xs text-white/70">
                  Address type:{' '}
                  <span className="font-semibold text-white/85">
                    {addressKind === 'solana' ? 'Solana' : addressKind === 'evm' ? 'EVM' : 'Unknown'}
                  </span>
                  <span className="text-white/50"> · We use this to restrict target chains and assets.</span>
                </div>

                <label className="grid gap-2">
                  <span className="text-xs text-white/60">Target asset</span>
                  <select
                    aria-label="Target asset"
                    value={target}
                    onChange={(e) => setTarget(e.target.value as AssetOptionId)}
                    className="h-11 rounded-xl border border-white/10 bg-black/20 px-4 text-sm text-white outline-none transition focus:border-primary/40"
                  >
                    {targetOptions.map((o) => (
                      <option key={o.id} value={o.id} className="bg-bg">
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="grid gap-2">
                  <span className="text-xs text-white/60">Target amount</span>
                  <input
                    aria-label="Target amount"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    inputMode="decimal"
                    placeholder="1.0"
                    className="h-11 rounded-xl border border-white/10 bg-black/20 px-4 text-sm text-white outline-none transition focus:border-primary/40"
                  />
                </label>

                <label className="grid gap-2">
                  <span className="text-xs text-white/60">Pay with asset (fees)</span>
                  <select
                    aria-label="Pay asset"
                    value={pay}
                    onChange={(e) => setPay(e.target.value as AssetOptionId)}
                    className="h-11 rounded-xl border border-white/10 bg-black/20 px-4 text-sm text-white outline-none transition focus:border-primary/40"
                  >
                    {payOptions.map((o) => (
                      <option key={o.id} value={o.id} className="bg-bg">
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="rounded-xl border border-white/10 bg-white/5 p-3 text-xs text-white/70">
                  Estimated fees: {formatFeeUsd(feeInfo.selectedFeeUsd)}
                  <span className="text-white/50">
                    {' '}
                    · Recommended: {feeInfo.recommended.option.label} ({formatFeeUsd(feeInfo.recommended.feeUsd)})
                  </span>
                  {!wallet.connection ? <span className="text-white/50"> · Connect wallet to narrow pay options.</span> : null}
                </div>

                <div className="mt-2 flex flex-wrap gap-2">
                  <Button onClick={generatePlan} disabled={!canGenerate}>
                    Generate plan
                  </Button>
                  <Button variant="ghost" onClick={() => (setPlan(null), setReceipt(null), setError(null))}>
                    Reset
                  </Button>
                </div>

                {error ? <div className="text-xs text-red-300">{error}</div> : null}
                <div className="text-xs text-white/45">This is a demo UI. It shows the flow and artifacts (plan + receipt).</div>
              </div>
            </Card>

            <Card variant="glass" className="p-7 md:col-span-7">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm font-semibold text-white/90">Plan</div>
                  <div className="mt-1 text-xs text-white/55">
                    {plan ? `Order ${shorten(plan.orderId, 8, 6)} · Deliver to ${shorten(plan.recipient)}` : 'Generate a plan to preview steps.'}
                  </div>
                </div>
                {plan ? (
                  <span className="rounded-full bg-glass-gradient px-3 py-1 text-xs font-semibold text-white/80">
                    {planBadge(plan)}
                  </span>
                ) : null}
              </div>

              <div className="mt-6 grid gap-3">
                {plan ? (
                  plan.steps.map((s) => (
                    <div key={s.id} className="rounded-xl border border-white/10 bg-white/5 p-4">
                      <div className="flex items-center justify-between gap-4">
                        <div className="text-sm font-semibold text-white/85">{s.title}</div>
                        <div className="text-xs text-white/50">{chainLabel[s.chain]}</div>
                      </div>
                      <div className="mt-2 text-xs text-white/60">{s.note}</div>
                    </div>
                  ))
                ) : (
                  <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-white/60">
                    No plan yet.
                  </div>
                )}
              </div>

              <div className="mt-6 flex flex-wrap gap-2">
                <Button variant="secondary" onClick={runDemoExecution} disabled={!plan}>
                  Run demo execution
                </Button>
              </div>

              {receipt ? (
                <div className="mt-7 rounded-xl border border-white/10 bg-black/20 p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-sm font-semibold text-white/90">Receipt</div>
                      <div className="mt-1 text-xs text-white/55">{receipt.delivered}</div>
                    </div>
                    <span className="rounded-full bg-primary px-3 py-1 text-xs font-semibold text-black">{receipt.status}</span>
                  </div>
                  <div className="mt-4 grid gap-2 md:grid-cols-2">
                    <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                      <div className="text-xs text-white/50">Estimated total fees</div>
                      <div className="mt-1 text-xs font-semibold text-white/85">{receipt.totalFees}</div>
                    </div>
                    <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                      <div className="text-xs text-white/50">Steps</div>
                      <div className="mt-1 text-xs font-semibold text-white/85">{receipt.steps.length}</div>
                    </div>
                  </div>
                  <div className="mt-4 grid gap-2">
                    {receipt.steps.map((s) => (
                      <div key={s.id} className="rounded-xl border border-white/10 bg-white/5 p-3">
                        <div className="flex items-center justify-between gap-4">
                          <div className="text-xs font-semibold text-white/85">{s.title}</div>
                          <div className="text-xs text-white/55">{s.status}</div>
                        </div>
                        <div className="mt-1 text-xs text-white/45">tx: {shorten(s.txHash, 10, 10)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </Card>
          </div>
        </div>
      </section>
    </main>
  )
}
